import { Link, router } from '@inertiajs/react'
import { useState } from 'react'
import { formatLempiras } from '../../lib/format'

interface Account { id: number; name: string; slug: string; logo_url?: string | null; loyalty_points_enabled?: boolean }
interface Customer { id: number; name: string; email: string }
interface OrderItem { id: number; product_name: string; variant_name?: string; quantity: number; line_total_cents: number }
interface Order {
  id: number; state: string; total_cents: number; placed_at: string
  payment_method?: string; reference_number?: string; payment_proof_url?: string | null
  order_items: OrderItem[]
}
interface Props { account: Account; customer: Customer; orders: Order[] }

const STATE_LABELS: Record<string, { label: string; color: string }> = {
  pending: { label: 'Pendiente', color: 'bg-gray-100 text-gray-700' },
  awaiting_payment: { label: 'Esperando pago', color: 'bg-yellow-100 text-yellow-700' },
  confirming: { label: 'Pago en verificación', color: 'bg-blue-100 text-blue-700' },
  paid: { label: 'Pagado', color: 'bg-blue-100 text-blue-700' },
  ready_for_pickup: { label: 'Listo para recoger', color: 'bg-green-100 text-green-700' },
  shipped: { label: 'Enviado', color: 'bg-green-100 text-green-700' },
  completed: { label: 'Completado', color: 'bg-green-200 text-green-800' },
  cancelled: { label: 'Cancelado', color: 'bg-red-100 text-red-700' },
}

export default function PortalMyOrders({ account, customer, orders }: Props) {
  const [uploadingRef, setUploadingRef] = useState<string | null>(null)

  const handleFileChange = (referenceNumber: string, file: File | null) => {
    if (!file) return
    setUploadingRef(referenceNumber)
    const formData = new FormData()
    formData.append('payment_proof', file)
    router.post(`/${account.slug}/catalog/thanks/${referenceNumber}/proof`, formData, {
      forceFormData: true,
      preserveScroll: true,
      onFinish: () => setUploadingRef(null),
    })
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-[#202020] text-white">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
          <Link href={`/${account.slug}/catalog`} className="flex items-center gap-2 font-bold text-lg">
            {account.logo_url && <img src={account.logo_url} className="h-8 w-8 object-contain bg-white rounded" />}
            {account.name}
          </Link>
          <div className="flex gap-3 text-sm">
            {account.loyalty_points_enabled && (
              <Link href={`/${account.slug}/portal/points`} className="hover:text-[#FFC107]">Mis puntos</Link>
            )}
            <Link href={`/${account.slug}/portal/logout`} method="delete" as="button" className="hover:text-[#FFC107]">Cerrar sesión</Link>
          </div>
        </div>
      </header>

      <div className="max-w-3xl mx-auto px-4 py-6">
        <h1 className="text-2xl font-bold text-[#202020] mb-1">Hola, {customer.name}</h1>
        <p className="text-gray-500 mb-6">{orders.length} pedido{orders.length === 1 ? '' : 's'}</p>

        {orders.length === 0 ? (
          <div className="bg-white rounded-lg shadow p-12 text-center">
            <p className="text-gray-500 mb-3">No tienes pedidos aún</p>
            <Link href={`/${account.slug}/catalog`} className="text-[#55ACEE] hover:underline">Ir al catálogo</Link>
          </div>
        ) : (
          <div className="space-y-3">
            {orders.map((o) => {
              const s = STATE_LABELS[o.state] || STATE_LABELS.pending
              return (
                <div key={o.id} className="bg-white rounded-lg shadow p-4">
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <p className="font-semibold text-[#202020]">Pedido {o.reference_number && <strong className="font-mono">{o.reference_number}</strong>}</p>
                      <p className="text-xs text-gray-500">{new Date(o.placed_at).toLocaleString('es-HN')}</p>
                    </div>
                    <span className={`text-xs font-semibold px-2 py-1 rounded ${s.color}`}>{s.label}</span>
                  </div>
                  <div className="text-sm text-gray-700 space-y-0.5">
                    {o.order_items.slice(0, 3).map((i) => (
                      <p key={i.id}>{i.quantity}× {i.product_name}</p>
                    ))}
                    {o.order_items.length > 3 && <p className="text-xs text-gray-500">+{o.order_items.length - 3} más</p>}
                  </div>
                  <div className="mt-2 flex items-end justify-between">
                    <div className="text-xs text-gray-500">
                      {o.reference_number && <span>Ref: <strong className="font-mono">{o.reference_number}</strong></span>}
                    </div>
                    <p className="font-bold text-[#55ACEE]">{formatLempiras(o.total_cents)}</p>
                  </div>

                  {/* Upload proof for transfer orders awaiting payment */}
                  {o.payment_method === 'transfer' && (o.state === 'awaiting_payment' || o.state === 'confirming') && (
                    <div className="mt-3 pt-3 border-t border-gray-100">
                      {o.payment_proof_url ? (
                        <div className="flex items-center gap-2 text-sm">
                          <span className="text-green-600">✅ Comprobante subido</span>
                          <a href={o.payment_proof_url} target="_blank" rel="noopener noreferrer"
                            className="text-[#55ACEE] hover:underline">Ver</a>
                        </div>
                      ) : (
                        <label className="flex items-center gap-2 text-sm cursor-pointer">
                          <span className="text-gray-600">
                            {uploadingRef === o.reference_number ? '⏳ Subiendo...' : '📎 Subir comprobante'}
                          </span>
                          <input type="file" accept="image/*,.pdf"
                            className="hidden"
                            disabled={uploadingRef === o.reference_number}
                            onChange={(e) => handleFileChange(o.reference_number!, e.target.files?.[0] || null)} />
                        </label>
                      )}
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        )}
      </div>
    </div>
  )
}
